import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { FaSpinner } from 'react-icons/fa';
import { IoIosArrowBack } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';

import brandImage from '@/assets/haggle-logo.png';
import { useUser } from '@/hooks/useUser';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { HaggleType } from '@/interfaces/api/IHaggleEvent';
import { VendorHaggleSummary } from '@/interfaces/api/haggle-event/IVendorHaggleSummary';
import { HaggleInviteStatus } from '@/interfaces/api/haggle-invite/haggle-invite.enum';
import { HaggleInvite } from '@/interfaces/api/haggle-invite/haggleInvite';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const B2BLiveVendors = () => {
	const { user } = useUser();
	const navigate = useNavigate();

	const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [verifiedHaggles, setVerifiedHaggles] = useState<number[]>([]);

	const { data: vendors, isLoading: vendorsLoading } = useQuery<
		VendorHaggleSummary[]
	>({
		queryKey: [ApiQueryKeys.getLiveVendors],
		queryFn: () => haggleApiService.getAllHaggleVendors(),
		refetchOnWindowFocus: false,
	});

	const { data: userInvites = [], refetch: refetchUserInvites } = useQuery<
		HaggleInvite[]
	>({
		queryKey: [ApiQueryKeys.getInvitesByEmail, user?.username],
		queryFn: () => haggleApiService.getHaggleInvitesByEmail(user!.username),
		enabled: !!user?.username,
		refetchOnWindowFocus: false,
	});

	const isOwner = useCallback(
		(vendorId: number) => vendorId === user?.id,
		[user?.id],
	);

	const redirectToB2BMarketPlace = useCallback(
		(storeName: string, haggleId: number) => {
			navigate('/user/b2b-marketplace', {
				state: { vendor: storeName, haggleId: haggleId },
			});
		},
		[navigate],
	);

	const getHaggleStatus = useCallback(
		(haggleId: number): HaggleInviteStatus | undefined => {
			const status = userInvites.find(
				(invite) => invite.haggleEventId === haggleId,
			)?.status;
			return status as HaggleInviteStatus | undefined;
		},
		[userInvites],
	);

	const handleJoinClick = async (haggleId: number, vendorId: number) => {
		if (!user?.username)
			return notificationService.error('Please sign in first.');

		try {
			await haggleApiService.createHaggleInvite({
				haggleEventId: haggleId,
				merchantId: vendorId,
				invitedEmails: [user.username],
				haggleType: HaggleType.SEMI_PRIVATE,
			});
			notificationService.success('Invite request sent successfully.');
			refetchUserInvites();
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				error?.message ||
				'Failed to send invite request.';
			notificationService.error(message);
		}
	};

	const handlePasswordSubmit = async (haggleId: number, storeName: string) => {
		try {
			const isValid = await haggleApiService.verifyHagglePassword(
				haggleId,
				password,
			);
			if (isValid) {
				setVerifiedHaggles((prev) => [...prev, haggleId]);
				notificationService.success('Password accepted');
				refetchUserInvites();
				redirectToB2BMarketPlace(storeName, haggleId);
			} else {
				notificationService.error('Invalid password. Please try again.');
			}
		} catch {
			notificationService.error(
				'Something went wrong while verifying password.',
			);
		}
	};

	const selectedVendorData = useMemo(
		() =>
			vendors?.find((v: VendorHaggleSummary) => v.storeName === selectedVendor),
		[vendors, selectedVendor],
	);

	return (
		<div className="flex flex-col gap-6">
			{vendorsLoading && (
				<div className="w-full h-[80vh] flex justify-center items-center">
					<FaSpinner size={50} className="animate-spin text-gray-600" />
				</div>
			)}

			{!vendorsLoading && vendors && vendors.length === 0 && (
				<div className="flex flex-col flex-grow justify-center items-center w-full bg-white rounded-lg overflow-hidden shadow-md min-h-[calc(100vh-125px)]">
					<div className="flex flex-col items-center justify-center text-grey-90 text-center px-6 py-12">
						<h2 className="text-lg font-semibold mb-2">
							No Live Haggle Events
						</h2>
						<p className="text-grey-50 max-w-md">
							There are currently no haggle events available. Please check back
							later or follow your favourite vendors for updates.
						</p>
					</div>
				</div>
			)}

			{!selectedVendor && (
				<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
					{vendors?.map((vendor: VendorHaggleSummary) => (
						<div
							key={vendor.storeName}
							className="opacity-90 bg-gradient-to-t from-gray-200 rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition"
							onClick={() => setSelectedVendor(vendor.storeName)}
						>
							<div className="w-full h-72 overflow-hidden">
								<img
									src={vendor?.tradeLogoUrl ?? brandImage}
									alt={vendor.storeName}
									className="w-full h-full object-contain object-center p-4"
								/>
							</div>
							<div className="p-4">
								<h2 className="text-base font-semibold text-gray-900 truncate">
									{vendor.vendor ?? vendor.storeName}
								</h2>
								<p className="text-sm text-gray-500 truncate">
									Trade Name: {vendor.tradeName}
								</p>
								<p className="text-sm text-gray-500 truncate">
									Live Events: {vendor.haggleEvents.length}
								</p>
							</div>
						</div>
					))}
				</div>
			)}

			{selectedVendor && selectedVendorData && (
				<div key={selectedVendorData.storeName}>
					<button
						onClick={() => setSelectedVendor(null)}
						className="mt-6 text-sm hover:text-blue-600 hover:underline flex items-center"
					>
						<IoIosArrowBack className="mr-2" />
						Back to all vendors
					</button>

					<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
						{selectedVendorData.haggleEvents.map((haggle) => {
							const status = getHaggleStatus(haggle.id);
							const isApproved = status === HaggleInviteStatus.APPROVED;
							const owner = isOwner(selectedVendorData.userId);
							const isVerified = verifiedHaggles.includes(haggle.id);
							const requiresAccess =
								(haggle.haggleType === HaggleType.SEMI_PRIVATE ||
									haggle.haggleType === HaggleType.PRIVATE) &&
								!isVerified &&
								!owner;

							const showAccessOverlay = requiresAccess || isVerified || owner;

							return (
								<div
									key={haggle.id}
									className="relative bg-white rounded-lg shadow-md overflow-hidden transition"
								>
									<div className="relative w-full h-72 overflow-hidden">
										<img
											src={selectedVendorData?.tradeLogoUrl ?? brandImage}
											alt={selectedVendorData.storeName}
											className={`w-full h-full object-contain object-center transition duration-300  p-4 ${
												showAccessOverlay ? 'blur-sm brightness-75' : ''
											}`}
										/>

										<div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-opacity-90 bg-gradient-to-t from-gray-400 text-white text-center p-4">
											{requiresAccess && (
												<>
													{status === HaggleInviteStatus.PENDING && (
														<button
															disabled
															className="text-yellow-600 font-semibold bg-yellow-50 px-4 py-2 rounded shadow cursor-not-allowed"
														>
															Pending Approval
														</button>
													)}
													{isApproved && (
														<div className="flex flex-col gap-2 w-full max-w-xs">
															<div className="relative w-full">
																<input
																	type={showPassword ? 'text' : 'password'}
																	value={password}
																	onChange={(e) => setPassword(e.target.value)}
																	className="border px-3 py-2 rounded w-full text-black"
																	placeholder="Enter password"
																/>
																<button
																	type="button"
																	onClick={() => setShowPassword(!showPassword)}
																	className="absolute inset-y-0 right-3 flex items-center text-black"
																>
																	{showPassword ? (
																		<AiOutlineEyeInvisible />
																	) : (
																		<AiOutlineEye />
																	)}
																</button>
															</div>
															<button
																onClick={() =>
																	handlePasswordSubmit(
																		haggle.id,
																		selectedVendorData.storeName,
																	)
																}
																className="bg-purpleBrand-500 text-white px-4 py-2 rounded"
															>
																Submit
															</button>
														</div>
													)}
													{!status && (
														<button
															onClick={() =>
																handleJoinClick(
																	haggle.id,
																	selectedVendorData.userId,
																)
															}
															className="text-purpleBrand-500 font-semibold bg-white px-4 py-2 rounded shadow hover:bg-gray-100 transition"
														>
															Request to Join
														</button>
													)}
												</>
											)}

											{(haggle.haggleType === HaggleType.PUBLIC ||
												isVerified ||
												owner) && (
												<button
													onClick={() =>
														redirectToB2BMarketPlace(
															selectedVendorData.storeName,
															haggle.id,
														)
													}
													className="text-purpleBrand-500 font-semibold bg-white px-4 py-2 rounded shadow hover:bg-gray-100 transition"
												>
													See Products
												</button>
											)}
										</div>
									</div>

									<div className="p-4">
										<h3 className="font-medium text-lg">{haggle.name}</h3>
										<p className="text-sm text-gray-600">
											{haggle.haggleType === HaggleType.PUBLIC
												? 'Public Haggle Event'
												: haggle.haggleType === HaggleType.SEMI_PRIVATE
												? 'Semi-Private Haggle Event'
												: 'Private Haggle Event'}
										</p>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
};

export default B2BLiveVendors;
